'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface RealtimeMessage {
  id: string;
  room_id: string;
  sender_id: string | null;
  sender_name: string;
  content: string;
  created_at: string;
  parent_message_id?: string | null;
}

export function useRoomMessages(roomId: string) {
  const [messages, setMessages] = useState<RealtimeMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, userProfile } = useAuth();
  const supabase = useMemo(() => createClient(), []);

  // Fetch initial messages (top-level only — replies fetched separately)
  useEffect(() => {
    if (!roomId) return;
    setLoading(true);

    supabase
      .from('room_messages')
      .select('*')
      .eq('room_id', roomId)
      .is('parent_message_id', null)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        setMessages(data ?? []);
        setLoading(false);
      });
  }, [roomId]);

  // Subscribe to real-time inserts (top-level messages only)
  useEffect(() => {
    if (!roomId) return;

    const channel = supabase
      .channel(`room-messages-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'room_messages',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          const newMsg = payload.new as RealtimeMessage;
          // Only add to top-level list if it's not a reply
          if (!newMsg.parent_message_id) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId]);

  // Send message with @mention detection
  const sendMessage = useCallback(
    async (content: string, parentMessageId?: string) => {
      if (!user || !content.trim()) return;

      const senderName = userProfile?.fullName || user.email || 'Unknown';

      const insertData: any = {
        room_id: roomId,
        sender_id: user.id,
        sender_name: senderName,
        content: content.trim(),
      };

      if (parentMessageId) {
        insertData.parent_message_id = parentMessageId;
      }

      const { data: msg, error } = await supabase
        .from('room_messages')
        .insert(insertData)
        .select()
        .single();

      if (error || !msg) return;

      // Detect @mentions and insert into message_mentions
      const mentionRegex = /@([A-Za-z]+(?:\s[A-Za-z]+)?)/g;
      const mentions = [...content.matchAll(mentionRegex)].map((m) => m[1]);

      if (mentions.length > 0) {
        const { data: mentionedUsers } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('full_name', mentions);

        if (mentionedUsers && mentionedUsers.length > 0) {
          const mentionRows = mentionedUsers
            .filter((u) => u.id !== user.id)
            .map((u) => ({
              message_id: msg.id,
              mentioned_user_id: u.id,
              room_id: roomId,
            }));

          if (mentionRows.length > 0) {
            await supabase.from('message_mentions').insert(mentionRows);
          }
        }
      }

      return msg;
    },
    [user, userProfile, roomId]
  );

  return { messages, loading, sendMessage };
}

export function useMessageReplies(messageId: string | null) {
  const [replies, setReplies] = useState<RealtimeMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    if (!messageId) { setReplies([]); return; }
    setLoading(true);

    supabase
      .from('room_messages')
      .select('*')
      .eq('parent_message_id', messageId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        setReplies(data ?? []);
        setLoading(false);
      });
  }, [messageId]);

  // Subscribe to new replies in real-time
  useEffect(() => {
    if (!messageId) return;

    const channel = supabase
      .channel(`replies-${messageId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'room_messages',
          filter: `parent_message_id=eq.${messageId}`,
        },
        (payload) => {
          const newReply = payload.new as RealtimeMessage;
          setReplies((prev) => {
            if (prev.some((r) => r.id === newReply.id)) return prev;
            return [...prev, newReply];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [messageId]);

  return { replies, loading };
}

export function useRoomUnreadCounts() {
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const { user } = useAuth();
  const supabase = useMemo(() => createClient(), []);
  const channelNameRef = useRef(`unread-counts-watcher-${Math.random().toString(36).slice(2)}`);

  const fetchUnreadCounts = useCallback(async () => {
    if (!user) return;

    const { data: receipts } = await supabase
      .from('room_read_receipts')
      .select('room_id, last_read_at')
      .eq('user_id', user.id);

    const readMap: Record<string, string> = {};
    receipts?.forEach((r) => {
      readMap[r.room_id] = r.last_read_at;
    });

    const { data: rooms } = await supabase.from('rooms').select('id');
    if (!rooms) return;

    const counts: Record<string, number> = {};

    await Promise.all(
      rooms.map(async (room) => {
        const lastRead = readMap[room.id];
        let query = supabase
          .from('room_messages')
          .select('id', { count: 'exact', head: true })
          .eq('room_id', room.id)
          .is('parent_message_id', null)
          .neq('sender_id', user.id);

        if (lastRead) {
          query = query.gt('created_at', lastRead);
        }

        const { count } = await query;
        counts[room.id] = count ?? 0;
      })
    );

    setUnreadCounts(counts);
  }, [user]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(channelNameRef.current)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'room_messages' },
        (payload) => {
          const msg = payload.new as RealtimeMessage;
          if (msg.sender_id !== user.id && !msg.parent_message_id) {
            setUnreadCounts((prev) => ({
              ...prev,
              [msg.room_id]: (prev[msg.room_id] ?? 0) + 1,
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const markRoomRead = useCallback(
    async (roomId: string) => {
      if (!user) return;
      await supabase.from('room_read_receipts').upsert(
        { user_id: user.id, room_id: roomId, last_read_at: new Date().toISOString() },
        { onConflict: 'room_id,user_id' }
      );
      setUnreadCounts((prev) => ({ ...prev, [roomId]: 0 }));
    },
    [user]
  );

  return { unreadCounts, markRoomRead, refetch: fetchUnreadCounts };
}
